// Updated App.js with integrated chat conversations in sidebar and payment routes

import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import UploadPage from "./components/UploadPage";
import ChatPage from "./components/ChatPage";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import EmailVerification from "./components/auth/EmailVerification";
import VerificationPending from "./components/auth/VerificationPending";
import SubscriptionRequired from "./components/subscription/SubscriptionRequired";
import UserManagement from "./components/admin/UserManagement";
import LandingPage from "./components/LandingPage";
import ThemeToggle from "./components/ThemeToggle";
import GoogleTranslateWidget from './components/GoogleTranslateWidget';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import darkLogo from "./ai-poreznik.png";
import lightLogo from "./ai-poreznik-dark.png";
import SettingsPage from "./components/SettingsPage";
// Import new payment components
import PaymentOptions from "./components/subscription/PaymentOptions";
import BankInstructionsPage from "./components/subscription/BankInstructionsPage";
import PaymentSuccessPage from "./components/subscription/PaymentSuccessPage";
import PaymentFailurePage from "./components/subscription/PaymentFailurePage";

const API_URL = process.env.REACT_APP_API_URL || 'https://worthy-optimism-production.up.railway.app/api';

const Logo = () => {
  const { logoSrc } = useTheme();
  const logoToUse = logoSrc === 'ai-poreznik.png' ? darkLogo : lightLogo;
  
  return (
    <img src={logoToUse} alt="Logo" width="220" style={{ verticalAlign: "middle" }} />
  );
};

// Protected route component
const ProtectedRoute = ({ children, requireAdmin, requireSubscription }) => {
  const { currentUser, hasRole, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-indicator" style={{ margin: '3rem auto', textAlign: 'center' }}>
        <div className="dots">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
        <p>Loading...</p>
      </div>
    );
  }

  // Not logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/app/login" />;
  }

  // Admin route check
  if (requireAdmin && !hasRole('admin')) {
    return <Navigate to="/app/chat" />;
  }

  // Subscription check - Note: This logic might need adjustment based on how subscription status is determined post-payment
  // if (requireSubscription && !hasActiveSubscription() && !hasRole('admin')) {
  //   return <Navigate to="/app/subscription-required" />;
  // }

  return children;
};

// Auth route (redirects to chat if already logged in)
const AuthRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-indicator" style={{ margin: '3rem auto', textAlign: 'center' }}>
        <div className="dots">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
        <p>Učitavanje ...</p>
      </div>
    );
  }

  if (currentUser) {
    return <Navigate to="/app/chat" />;
  }

  return children;
};

// Integrated Sidebar Component with Chat History
const Sidebar = ({ isOpen, toggleSidebar, currentConversationId, setCurrentConversationId }) => {
  const { currentUser, logout, hasRole } = useAuth();
  const { logoSrc } = useTheme();
  const logoToUse = logoSrc === 'ai-poreznik.png' ? darkLogo : lightLogo;
  const isAdmin = hasRole('admin');
  
  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);

  // Format date for conversation list
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('bs', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    }).format(date);
  };

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!currentUser) return;
    
    setLoadingConversations(true);
    try {
      const response = await axios.get(`${API_URL}/conversations`);
      if (response.data && response.data.success) {
        setConversations(response.data.conversations || []);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [currentUser]);

  // Initial fetch
  useEffect(() => {
    fetchConversations();
    
    // Listen for refreshConversations events
    const refreshHandler = (event) => {
      fetchConversations();
    };
    
    window.addEventListener('refreshConversations', refreshHandler);
    
    return () => {
      window.removeEventListener('refreshConversations', refreshHandler);
    };
  }, [fetchConversations]);

  const startNewChat = () => {
    setCurrentConversationId(null);
    toggleSidebar();
  };

  const selectConversation = (id) => {
    setCurrentConversationId(String(id));
    toggleSidebar();
  };

  // eslint-disable-next-line no-unused-vars
  const deleteConversation = async (e, id) => {
    e.stopPropagation(); // Prevent triggering the conversation selection
    
    if (!window.confirm('Jeste li sigurni da želite izbrisati ovaj razgovor?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/conversations/${id}`);
      
      // Update conversations list
      setConversations(prevConversations => 
        prevConversations.filter(conv => conv.id !== id)
      );
      
      // If the deleted conversation was the current one, reset
      if (currentConversationId === String(id)) {
        setCurrentConversationId(null);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Greška pri brisanju razgovora. Molimo pokušajte ponovno.');
    }
  };
  
  return (
    <>
      <div className={`claude-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img src={logoToUse} alt="Logo" width="160" className="sidebar-logo" />
        </div>
        
        <div className="sidebar-content">
          {/* Main Navigation */}
          <nav className="sidebar-nav">
            <ul>
              <li>
                <Link to="/app/chat" className="sidebar-nav-item" onClick={toggleSidebar}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <span>Chat</span>
                </Link>
              </li>
              {/* <li>
                <Link to="/app/subscribe" className="sidebar-nav-item" onClick={toggleSidebar}>
                   <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                     <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                     <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                   </svg>
                  <span>Pretplata</span>
                </Link>
              </li> */}
              
              {isAdmin && (
                <>
                  <li>
                    <Link to="/app/upload" className="sidebar-nav-item" onClick={toggleSidebar}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>Upload</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/app/admin" className="sidebar-nav-item" onClick={toggleSidebar}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span>CMS</span>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </nav>
          
          {/* Chat History Section */}
          <div className="chat-history-section">
            <div className="chat-history-header">
              <h3>Razgovori</h3>
              <button 
                className="new-chat-button" 
                onClick={startNewChat}
                title="New Chat"
                aria-label="Novi razgovor"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>Novi</span>
              </button>
            </div>
            
            <div className="chat-history-list">
              {loadingConversations ? (
                <div className="loading-indicator" style={{ margin: '1rem auto', textAlign: 'center' }}>
                  <div className="dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="no-chat-history">
                  <p>Nema razgovora</p>
                </div>
              ) : (
                conversations.map(conversation => (
                  <div 
                    key={conversation.id} 
                    className={`chat-history-item ${currentConversationId === String(conversation.id) ? 'active' : ''}`}
                    onClick={() => selectConversation(conversation.id)}
                  >
                    <div className="chat-history-title">
                      {conversation.title || 'Razgovor bez naslova'}
                    </div>
                    <div className="chat-history-meta">
                      <span className="chat-history-date">{formatDate(conversation.created_at)}</span>
                      {/* <button 
                        className="delete-chat" 
                        onClick={(e) => deleteConversation(e, conversation.id)}
                        title="Delete conversation"
                        aria-label="Izbriši razgovor"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button> */}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        
        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-actions">
            <ThemeToggle />
            <Link 
              to="/app/settings" 
              className="sidebar-settings-button"
              title="Postavke"
              aria-label="Postavke"
              onClick={toggleSidebar}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </Link>
            <button 
              className="sidebar-logout-button" 
              onClick={logout}
              title="Odjava"
              aria-label="Odjava"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <button 
        className={`sidebar-toggle ${isOpen ? 'active' : ''}`} 
        onClick={toggleSidebar}
        aria-label="Toggle Sidebar"
      >
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        )}
      </button>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { currentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Router>
      <div className="App">
        {currentUser && 
          <Sidebar 
            isOpen={sidebarOpen} 
            toggleSidebar={toggleSidebar} 
            currentConversationId={currentConversationId} 
            setCurrentConversationId={setCurrentConversationId} 
          />}
        
        <header className={`App-header ${currentUser ? 'with-sidebar' : ''}`}>
          {!currentUser && (
            <div className="model-selector">
              <Logo />
            </div>
          )}

          {!currentUser && (
            <>
              <Link to="/app/login" className="login-button">Prijava</Link>
              <Link to="/app/register" className="register-button">Registracija</Link>
            </>
          )}
        </header>

        {/* Google Translate Widget - positioned below header */}
        <div className="translate-widget-container">
          <GoogleTranslateWidget />
        </div>

        <main className={`App-main ${currentUser ? 'with-sidebar' : ''}`}>
          <Routes>
            {/* Public Landing Page */}
            <Route path="/" element={<LandingPage />} />

            {/* Auth Routes */}
            <Route path="/app/login" element={<AuthRoute><Login /></AuthRoute>} />
            <Route path="/app/register" element={<AuthRoute><Register /></AuthRoute>} />
            <Route path="/app/verify-email" element={<EmailVerification />} />
            <Route path="/app/verification-pending" element={<VerificationPending />} />

            {/* Protected Routes */}
            <Route 
              path="/app/chat" 
              element={
                <ProtectedRoute requireSubscription={true}>
                  <ChatPage 
                    currentConversationId={currentConversationId} 
                    setCurrentConversationId={setCurrentConversationId} 
                  />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/app/chat/:conversationId" 
              element={
                <ProtectedRoute requireSubscription={true}>
                  <ChatPage 
                    currentConversationId={currentConversationId} 
                    setCurrentConversationId={setCurrentConversationId} 
                  />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/app/upload" 
              element={<ProtectedRoute requireAdmin={true}><UploadPage /></ProtectedRoute>} 
            />
            <Route 
              path="/app/admin" 
              element={<ProtectedRoute requireAdmin={true}><UserManagement /></ProtectedRoute>} 
            />
            <Route 
              path="/app/settings" 
              element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} 
            />
            <Route 
              path="/app/subscription-required" 
              element={<SubscriptionRequired />} 
            />
            {/* Payment Routes */}
            <Route 
              path="/app/subscribe" 
              element={<ProtectedRoute><PaymentOptions /></ProtectedRoute>} 
            />
            <Route 
              path="/app/bank-instructions" 
              element={<ProtectedRoute><BankInstructionsPage /></ProtectedRoute>} 
            />
            <Route 
              path="/app/payment-success" 
              element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} 
            />
            <Route 
              path="/app/payment-failed" 
              element={<ProtectedRoute><PaymentFailurePage /></ProtectedRoute>} 
            />

            {/* Redirect root of /app to chat if logged in, otherwise to login */}
            <Route 
              path="/app" 
              element={currentUser ? <Navigate to="/app/chat" /> : <Navigate to="/app/login" />}
            />

            {/* Catch-all for unknown /app routes */}
            <Route path="/app/*" element={<Navigate to="/app/chat" />} />

          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;

